import BaseService from './BaseService';
import RestService from './RestService';

import { dataTypes } from "./Constants";
import PlaceCard from '../data/PlaceCard';
import HotelCard from '../data/HotelCard';
import RouteCard from '../data/RouteCard';
import StageCard from '../data/StageCard';
import RouteList from '../data/RouteList';
import HotelList from '../data/HotelList';
import POIList from '../data/POIList';
import PageData from '../data/PageData';
import AnalyticsAPIService from "./AnalyticsAPIService";
import PoiCard from "../data/PoiCard";
import { LocaleService } from "./LocaleService";
import type CardData from '../data/CardData';
import type { CardProperties, CardRawData } from '../data/CardData';
import type { RouteListItem } from '../data/RouteData';
import type { HotelListItem } from '../data/HotelData';
import type { POIListItem } from '../data/POIData';
import type { PageRawData } from '../data/PageData';
import type { HotelCardRawData } from '../data/HotelCard';
import type { PlaceCardRawData } from '../data/PlaceCard';
import type { RouteCardRawData } from '../data/RouteCard';
import type { StageCardRawData } from '../data/StageCard';
import type { PoiCardRawData } from '../data/PoiCard';
import type PlaceData from '../data/PlaceData';
import type { LngLatBounds } from 'mapbox-gl';
/**
 * Provides back-end data and data mapping.
 * - Load objects from the backend.
 * - Manage data cachig and storage.
 *
 * Every API method here must return straight or Promise-wrapped data
 * that resolves to some data object or null.
 */
class DataService extends BaseService {

    static routeList: RouteList | undefined;

    /**
     * Initialize, preload route list.
     */
    static init(): void {
        this.loadRouteList()
            .then(routeList => { if (routeList) this.setRouteList(routeList); });
    }

    /**
     * Sets route list.
     */
    static setRouteList(routeList: RouteList): void {
        this.routeList = routeList;
    }

    /**
     * Gets route by key.
     */
    static getRouteByKey(key: string | number) {
        this.debug("key ", key);
        return this.routeList && this.routeList.getPlace(key);
    }

    /**
     * Create card data.
     *
     * @param type Data type
     * @param data Data from REST, may be an object or an array
     */
    static createCardData(
        type: string,
        data: CardRawData | CardRawData[],
        restUrl: string,
        name = '',
    ): CardData | undefined {
        const properties: CardProperties = {
            name,
            url: restUrl,
        };

        // Data may be an object or an array.
        const objectData = Array.isArray(data) ? data.shift() : data;

        this.debug("Creating card data / " + type, objectData);
        // El switch sobre `type` es el discriminante: el backend devuelve la
        // forma que corresponde al tipo pedido, pero eso no se valida aqui.
        switch (type) {
            case dataTypes.hotel:
                return new HotelCard(type, properties, objectData as HotelCardRawData);
            case dataTypes.place:
                return new PlaceCard(type, properties, objectData as PlaceCardRawData);
            case dataTypes.route:
                return new RouteCard(type, properties, objectData as RouteCardRawData);
            case dataTypes.stage:
                return new StageCard(type, properties, objectData as StageCardRawData);
            case dataTypes.poi:
                return new PoiCard(type, properties, objectData as PoiCardRawData);
            default:
                this.logger().error("Unknown data type", type);
        }

    }

    /*
* Get route list
*
* @return Parsed response body if any, none if empty
*/
    static async loadRouteList(): Promise<RouteList | undefined> {
        const listItems = await RestService.getRouteList();
        this.debug("listItems ", listItems);
        if (listItems) {
            return RouteList.fromListItems(listItems as unknown as RouteListItem[]);
        }
    }

    /**
     * Load from services by object type, id.
     */
    static async loadFromPlaceData(placeData: PlaceData) {
        let url;
        if (placeData.restUrl) {
            url = placeData.restUrl;
        }
        else if (placeData.type === dataTypes.stage && placeData.isMapFeature()) {
            // getMapItemId() puede devolver undefined; la concatenacion de
            // getUrlForDataType ya lo convertia a cadena.
            url = RestService.getUrlForDataType(dataTypes.track, String(placeData.getMapItemId()));
        }
        else {
            url = RestService.getUrlForDataType(placeData.type, placeData.id);
        }

        const queryString = LocaleService.getLanguage() !== 'es' ? `lang=${LocaleService.getLanguage()}` : '';

        return RestService.getRawData(url, queryString, true)
            .then(res => {
                return res ? this.createCardData(placeData.type, res as CardRawData, url, placeData.name) : null;
            }, error => { this.logger().error("Cannot load place data", error) });
    }

    /**
     * Load accommodations from rest API
     *
     * @param bounds
     *   Map bounds, https://docs.mapbox.com/mapbox-gl-js/api/geography/#lnglatbounds
     */
    static async loadHotels(bounds: LngLatBounds) {
        this.debug('Map.loadHotels', bounds);

        return RestService.getHotels(bounds)
            .then(listItems => {
                // this.debug("listItems", listItems);
                return listItems ? HotelList.fromListItems(listItems as unknown as HotelListItem[]) : null;
            }, error => { this.logger().error("Cannot load hotel list", error) });

    }

    /**
     * Get text page by Name.
     */
    static async getPageByName(name: string, label: string) {
        const pages = PageData.getSitePages();

        if (name in pages) {
            return this.getPageById(pages[name].id, label);
        }
        else {
            return Promise.reject("Missing page: " + name);
        }
    }

    /**
     * Get text page by Id.
     *
     * @param id The page id = Drupal node id
     */
    static async getPageById(id: number | string, label: string) {
        const type = dataTypes.page;
        this.debug('DataService.getPage', id);
        const url = RestService.getUrlForDataType(type, id);
        const props = { type: type, id: id, url: url };
        return RestService.getRawData(url, null, true, false)
            .then(res => {
                AnalyticsAPIService.sendViewCard(
                    label,
                    url
                );
                return res ? new PageData(id, props, res as PageRawData) : null;
            }, error => {
                this.logger().error("Cannot load page", error);
            });
    }

    /**
     * Load POIS from rest API
     *
     * @param bounds
     *   Map bounds, https://docs.mapbox.com/mapbox-gl-js/api/geography/#lnglatbounds
     */
    static async loadPOIs(bounds: LngLatBounds): Promise<POIList | null> {
        this.debug('Map.loadPOIs', bounds);

        return RestService.getPOIs(bounds)
            .then(
                listItems => {
                    //this.debug("listItems", listItems);
                    const poiListItems = listItems ? listItems : [];
                    return POIList.fromListItems(poiListItems as unknown as POIListItem[]);
                },
                error => {
                    this.logger().error("Cannot load POI list", error);
                    return null;
                })
            .catch(error => {
                this.logger().error("Catch load POI list error", error);
                return null;
            });

    }
}

export default DataService;
